export type LockableChildSection = {
    key: string;
    label: string;
    description?: string;
};

export type LockableSection = {
    key: string;
    label: string;
    description: string;
    children?: LockableChildSection[];
};

export type LockState = 'locked' | 'partial' | 'unlocked';

export const DEFAULT_LOCKED_SECTIONS = ['centerReport', 'settlement'];

const LEGACY_LOCKED_SECTION_KEY_MAP: Record<string, string> = {
    notice: 'centerOps.notice',
    'centerOps.gifticon': 'gifticon',
};

export const LOCKABLE_SECTION_TREE: LockableSection[] = [
    {
        key: 'member',
        label: '회원/강사 관리',
        description: '회원 · 강사 리스트 및 소통 상자',
        children: [
            { key: 'member.memberManagement', label: '회원관리' },
            { key: 'member.coachManagement', label: '강사관리' },
            { key: 'member.communicationBox', label: '소통상자' },
        ],
    },
    {
        key: 'schedule',
        label: '스케줄 관리',
        description: '스케줄 캘린더 및 변경 이력',
        children: [
            { key: 'schedule.calendar', label: '스케줄 캘린더' },
            { key: 'schedule.changeCancel', label: '스케줄 변경 / 취소' },
            /*
              스케줄 동작 잠금(생성·수정·삭제). 보기(캘린더)와 독립이고 기본은 열림이다.
              서버는 lockDecisions(저장 키 scheduleCreate 등)로 따로 저장한다 — 배열에 넣으면 400.
              설계: rappo docs/SCHEDULE_ACTION_LOCK_DESIGN.md §6
            */
            {
                key: 'schedule.create',
                label: '수업 생성',
                description: '새 수업·일반·OFF·반복·그룹 회차를 만들 때',
            },
            {
                key: 'schedule.edit',
                label: '수업 수정',
                description: '시간·강사 변경, 출석·노쇼, 회원 추가·예약, 신청 승인·거절',
            },
            {
                key: 'schedule.delete',
                label: '수업 삭제',
                description: '수업 삭제(취소)와 그룹 폐강',
            },
        ],
    },
    {
        /*
          데일리 브리핑.

          ⚠️ 이 섹션의 기본값은 **잠금**이다 (DEFAULT_LOCKED_CLIENT_SECTIONS).
             회원 개인 데이터가 통째로 실리고, 이 탭 안에서 **선물(포인트)이 나간다.**
             명시적으로 열기 전까지는 잠긴 것으로 본다 — fail-closed.
        */
        key: 'opsBriefing',
        label: '손실 시그널',
        description: '오늘의 시그널 · 회원/강사 현황 · 지시 이력',
    },
    {
        key: 'centerReport',
        label: '센터 리포트',
        description: '센터 통계 및 리뷰 데이터',
        children: [
            { key: 'centerReport.reviewStats', label: '리뷰 통계' },
            { key: 'centerReport.centerStats', label: '센터 통계' },
            { key: 'centerReport.memberReviews', label: '회원 리뷰' },
            { key: 'centerReport.newOpportunity', label: '신규기회 분석' },
        ],
    },
    {
        key: 'feed',
        label: '수업 피드',
        description: '수업 일지 및 피드 데이터',
    },
    {
        key: 'classPass',
        label: '수강권 관리',
        description: '수강권 목록 및 관리',
    },
    {
        key: 'settlement',
        label: '매출 정산',
        description: '매출 및 정산 데이터',
        children: [
            { key: 'settlement.salesCalendar', label: '매출 달력' },
            { key: 'settlement.lessonSettlement', label: '수업료 정산' },
            { key: 'settlement.salesList', label: '매출 목록' },
            { key: 'settlement.unpaidList', label: '미수금 목록' },
            { key: 'settlement.salary', label: '급여 계산' },
        ],
    },
    {
        key: 'gifticon',
        label: '기프티콘 선물',
        description: '기프티콘 선물 발송 · 캠페인 · 자동화',
    },
    {
        key: 'centerOps',
        label: '센터 운영',
        description: '공지사항 · 시설 이용권 · 출석 · 락커 · 키오스크',
        children: [
            { key: 'centerOps.notice', label: '공지 사항' },
            { key: 'centerOps.facilityProduct', label: '시설 이용권 상품' },
            { key: 'centerOps.facilitySubscription', label: '이용권 현황 · 매출' },
            { key: 'centerOps.checkin', label: '회원 출석 관리' },
            { key: 'centerOps.locker', label: '락커 관리' },
            { key: 'centerOps.kioskSettings', label: '키오스크 설정' },
            { key: 'centerOps.tabletPromotion', label: '태블릿 홍보' },
            { key: 'centerOps.centerStats', label: '통계' },
        ],
    },
];

export const VALID_LOCKED_SECTION_KEYS = LOCKABLE_SECTION_TREE.flatMap((section) => [
    section.key,
    ...(section.children?.map((child) => child.key) ?? []),
]);

export const getParentSectionKey = (sectionKey: string) => sectionKey.split('.')[0];

/* ═══════════════════════════════════════════════════════════
   서버가 아는 키 · 아직 모르는 키
   ═══════════════════════════════════════════════════════════

   잠금 설정은 서버(admin.lockedSections)에 저장되는데, 서버는 **자기 enum 에 없는
   키를 받으면 요청 전체를 400 으로 거절합니다**(admin.validation.js 의 Joi valid()).
   조회할 때도 모르는 키는 조용히 걸러 냅니다.

   그래서 화면에만 있는 섹션을 그대로 섞어 보내면,
   그 요청에 함께 담긴 **다른 섹션 토글까지 통째로 저장에 실패**합니다.
   (실제로 지금 기프티콘 잠금이 이 상태였습니다 — 눌러도 아무 일이 없었습니다)

   그래서 둘로 나눕니다.
     · 서버가 아는 키 → 서버에 저장 (기기가 바뀌어도 따라온다)
     · 서버가 모르는 키 → 이 브라우저에 보관 (백엔드가 enum 을 늘리면 서버로 옮긴다)

   ⚠️ 클라이언트 보관은 **기기마다 따로**입니다. 다른 PC 에서는 기본값이 적용됩니다.
      선물처럼 돈이 나가는 동작은 그래서 기본값을 '잠금'으로 둡니다.
      서버 저장이 필요한 계약은 BACKEND_HANDOFF_OPS_BRIEFING_GIFT.md 에 적어 두었습니다. */

/**
 * 서버(rappo/src/constants/lockedSections.js)가 아는 키를 그대로 옮긴 것.
 * 여기 없는 키를 PATCH 로 보내면 400 이다.
 */
export const SERVER_KNOWN_LOCK_SECTION_KEYS = [
    'member',
    'member.memberManagement',
    'member.coachManagement',
    'member.communicationBox',
    'schedule',
    'schedule.calendar',
    'schedule.changeCancel',
    'centerReport',
    'centerReport.reviewStats',
    'centerReport.centerStats',
    'centerReport.memberReviews',
    'centerReport.newOpportunity',
    'feed',
    'classPass',
    'settlement',
    'settlement.salesCalendar',
    'settlement.lessonSettlement',
    'settlement.salesList',
    'settlement.unpaidList',
    'settlement.salary',
    'centerOps',
    'centerOps.facilityProduct',
    'centerOps.facilitySubscription',
    'centerOps.checkin',
    'centerOps.locker',
    'centerOps.kioskSettings',
    'centerOps.tabletPromotion',
    'centerOps.centerStats',
    /* 서버는 공지사항을 최상위 'notice' 로 안다 — 화면 키와 이름만 다르다 */
    'notice',
];

/**
 * 서버 결정(lockDecisions) 키 표 — 화면 섹션 키(sectionKey)와 저장·전송 키(storageKey)를 나눈다.
 *
 * ⚠️ 경계 규칙(예외 없음): 배열(lockedSections)은 **섹션 키**, 결정 객체(lockDecisions)는 **저장 키**.
 *    점(.) 있는 섹션 키를 결정 객체에 그대로 보내면 서버가 400 으로 거절한다.
 *    서버 표: rappo src/services/adminLockSections.service.ts DECISION_LOCKS
 */
export const DECISION_LOCKS = [
    { sectionKey: 'opsBriefing', storageKey: 'opsBriefing' },
    { sectionKey: 'gifticon', storageKey: 'gifticon' },
    { sectionKey: 'schedule.create', storageKey: 'scheduleCreate' },
    { sectionKey: 'schedule.edit', storageKey: 'scheduleEdit' },
    { sectionKey: 'schedule.delete', storageKey: 'scheduleDelete' },
] as const;

/** 결정 섹션 키 다섯 — 서버로 보내는 배열에서 전부 뺀다 */
export const DECISION_SECTION_KEYS = DECISION_LOCKS.map((d) => d.sectionKey);
export type DecisionSectionKey = (typeof DECISION_LOCKS)[number]['sectionKey'];
export type DecisionStorageKey = (typeof DECISION_LOCKS)[number]['storageKey'];

/**
 * 브라우저에도 결정을 기록하는 키 — 손실 시그널·기프티콘 둘뿐.
 * 서버 결정 저장 전부터 브라우저에 있던 키라, 구서버(legacy)에서의 판정·이관(1회) 대상이다.
 * 스케줄 동작 세 키는 브라우저에 기록하지 않는다(이력이 없어 이관도 없다).
 */
export const BROWSER_DECISION_KEYS = ['opsBriefing', 'gifticon'] as const;
export type BrowserDecisionKey = (typeof BROWSER_DECISION_KEYS)[number];

/** 스케줄 동작 키 — 판정 규칙이 기존 두 키와 다르다(legacy=열림, error·unknown=잠김) */
export const SCHEDULE_ACTION_SECTION_KEYS = ['schedule.create', 'schedule.edit', 'schedule.delete'] as const;
export type ScheduleActionSectionKey = (typeof SCHEDULE_ACTION_SECTION_KEYS)[number];
export const isScheduleActionSection = (key: string | null | undefined): key is ScheduleActionSectionKey =>
    !!key && (SCHEDULE_ACTION_SECTION_KEYS as readonly string[]).includes(key);

/**
 * 서버 enum 에 없는 섹션 중 **이 브라우저에 보관하는** 것.
 * 자동 분류가 서버 결정 전용 키(스케줄 동작)를 브라우저 전용으로 잡지 않게 그 키들은 뺀다.
 */
export const CLIENT_ONLY_LOCK_SECTION_KEYS = VALID_LOCKED_SECTION_KEYS.filter((key) => {
    if (SERVER_KNOWN_LOCK_SECTION_KEYS.includes(key) || key === 'centerOps.notice') return false;
    // 서버 결정 전용 키(스케줄 동작)는 브라우저에 두지 않는다
    if ((DECISION_SECTION_KEYS as readonly string[]).includes(key)) {
        return (BROWSER_DECISION_KEYS as readonly string[]).includes(key);
    }
    return true;
});

/**
 * 명시적으로 열기 전까지 **잠긴 것으로 보는** 섹션.
 *
 * 다른 섹션은 "목록에 없으면 열림"이지만, 이 섹션들은 반대다.
 * 설정을 한 번도 만진 적 없는 센터·처음 쓰는 PC 에서도 잠겨 있어야 한다.
 */
export const DEFAULT_LOCKED_CLIENT_SECTIONS = ['opsBriefing'];

/** 이 브라우저가 기억하는, 서버가 모르는 섹션의 잠금 여부 */
export type ClientLockDecisions = Record<string, boolean>;

const CLIENT_DECISION_KEY = 'lockedTabClientConfig';

/**
 * 결정은 **계정별로** 보관한다.
 *
 * 센터 데스크의 PC 는 여러 원장·직원이 같은 브라우저를 쓴다.
 * 계정 구분 없이 한 곳에 두면, A 원장이 "브리핑 열어 둠"으로 정한 것이
 * 같은 PC 로 로그인한 B 원장에게도 그대로 적용된다 — 잠금 기본값을
 * 잠금으로 둔 의미가 사라진다.
 *
 * 계정을 모르면(로그인 전 등) 공용 칸을 쓴다. 그 칸은 기본값(브리핑=잠금)만
 * 적용되므로 여는 쪽으로 새지 않는다.
 */
const scopeOf = (accountId?: string | null) => (accountId ? String(accountId) : '_shared');

type StoredDecisions = Record<string, ClientLockDecisions>;

const readStore = (): StoredDecisions => {
    try {
        const raw = localStorage.getItem(CLIENT_DECISION_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
        return parsed as StoredDecisions;
    } catch {
        return {};
    }
};

const pickDecisions = (value: unknown): ClientLockDecisions => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
    const decisions: ClientLockDecisions = {};
    CLIENT_ONLY_LOCK_SECTION_KEYS.forEach((key) => {
        const entry = (value as Record<string, unknown>)[key];
        if (typeof entry === 'boolean') decisions[key] = entry;
    });
    return decisions;
};

/**
 * 이 계정의 결정만 읽는다.
 *
 * ⚠️ 다른 계정 칸이나 예전 평평한 형태를 물려받지 않는다.
 *    같은 PC 를 여러 계정이 쓰는데 한쪽 결정을 물려주면, A 원장이 열어 둔 것이
 *    B 원장에게도 적용된다 — 계정별로 나눈 의미가 사라진다.
 *    (이 저장소 키는 이번에 새로 만든 것이라 물려받을 실사용 값도 없다.
 *     예전부터 있던 `lockedTabConfig` 배열은 `fallback` 인자로 따로 승계한다)
 */
export const readClientLockDecisions = (accountId?: string | null): ClientLockDecisions =>
    pickDecisions(readStore()[scopeOf(accountId)]);

export const writeClientLockDecisions = (decisions: ClientLockDecisions, accountId?: string | null) => {
    try {
        const store = readStore();
        const next: StoredDecisions = {};
        /*
         * 계정별 칸만 남긴다 — 예전의 평평한 형태(최상위에 섹션 키가 바로 있던 것)는
         * 여기서 사라진다. 승계는 readClientLockDecisions 가 **한 계정에** 못 박은 뒤이므로
         * 정보가 없어지는 것이 아니라 주인이 정해지는 것이다.
         */
        Object.entries(store).forEach(([key, value]) => {
            if (value && typeof value === 'object' && !Array.isArray(value)) next[key] = pickDecisions(value);
        });
        next[scopeOf(accountId)] = decisions;
        localStorage.setItem(CLIENT_DECISION_KEY, JSON.stringify(next));
    } catch {
        // 저장소 접근 불가(사파리 프라이빗 등) — 기본값(잠금)으로 동작한다
    }
};

/**
 * 이 브라우저 기준으로 잠긴 클라이언트 전용 섹션.
 *
 * `fallback` 은 "결정을 기억하지 못할 때 무엇을 볼 것인가"다.
 * 예전 저장분(lockedTabConfig)에 이미 들어 있던 키는 그 결정을 승계하고,
 * 그마저 없으면 DEFAULT_LOCKED_CLIENT_SECTIONS 를 따른다.
 */
export const clientOnlyLockedSections = (
    decisions: ClientLockDecisions = readClientLockDecisions(null),
    fallback: string[] = [],
): string[] =>
    CLIENT_ONLY_LOCK_SECTION_KEYS.filter((key) => {
        if (typeof decisions[key] === 'boolean') return decisions[key];
        if (fallback.includes(key)) return true;
        return DEFAULT_LOCKED_CLIENT_SECTIONS.includes(key);
    });

/**
 * 서버가 준(또는 서버로 보낼) 목록에 이 브라우저의 결정을 얹는다.
 *
 * 서버 응답에는 클라이언트 전용 키가 절대 들어 있지 않다.
 * 그대로 state 에 넣으면 조회가 한 번 돌 때마다 잠금이 풀린다.
 */
export const mergeClientLockDecisions = (
    serverSections: string[],
    decisions?: ClientLockDecisions,
    fallback: string[] = [],
): string[] =>
    normalizeLockedSections([
        ...serverSections.filter((key) => !CLIENT_ONLY_LOCK_SECTION_KEYS.includes(key)),
        ...clientOnlyLockedSections(decisions, fallback),
    ]);

/* ═══════════════════════════════════════════════════════════
   손실 시그널·기프티콘 — 서버 저장(새 계약 3)
   ═══════════════════════════════════════════════════════════

   서버가 두 키의 결정을 `admin.lockDecisions` 에 따로 저장한다
   (rappo docs/LOCK_SECTIONS_SERVER_SYNC_DESIGN.md v3).
   요청 헤더 `X-Rappo-Lock-Contract: 3` 을 보낸 요청에만 새 응답이 온다 —
   구서버(롤백)는 헤더를 무시하고 lockVersion 2 를 준다.

   지원 상태 3가지(계정별)
     · unknown — 이 계정의 잠금 조회 결과가 아직 없다. 두 키는 잠긴 것으로 본다(fail-closed).
     · v3      — 잠금 조회 응답이 lockVersion ≥ 3. 두 키도 서버 배열을 따른다.
     · legacy  — 조회 성공했는데 lockVersion < 3(구서버), 조회 실패, 조회 대상 아님. 지금 동작 그대로.

   ⚠️ 두 키는 `lockedSections` 배열에 **절대** 넣지 않는다 — 신·구 서버 모두 400 이다.
      결정은 `lockDecisions` 로, 바뀐 키만 보낸다. */

/**
 * 지원 상태.
 *  · unknown — 조회 결과 없음.
 *  · v3      — 조회 응답 lockVersion ≥ 3.
 *  · legacy  — 조회 성공했는데 구서버, 또는 조회 대상 아님(슈퍼관리자·계정 없음).
 *  · error   — 조회 실패(또는 판정 근거 없는 응답).
 * 기존 두 키(손실 시그널·기프티콘)는 error 를 legacy 와 같게 본다(기존 fallback 그대로).
 * 스케줄 동작 세 키는 error·unknown = 잠김, legacy = 열림(구서버는 이 잠금을 모른다).
 */
export type LockSupportState = 'unknown' | 'v3' | 'legacy' | 'error';

/** 서버 결정 원값 — **섹션 키** 기준으로 들고 있는다. null = 서버 미정(기본값 적용 중) */
export type ServerLockDecisions = Record<DecisionSectionKey, boolean | null>;

export const EMPTY_SERVER_LOCK_DECISIONS: ServerLockDecisions = {
    opsBriefing: null,
    gifticon: null,
    'schedule.create': null,
    'schedule.edit': null,
    'schedule.delete': null,
};

/** 이 섹션이 결정 키인가 — **섹션 키 정확 일치**(부모 기준이 아니다) */
export const isDecisionLockSection = (sectionKey: string | null | undefined): boolean =>
    !!sectionKey && (DECISION_SECTION_KEYS as readonly string[]).includes(sectionKey);

/** 잠금 응답 본문이 새 계약인가. 매 응답마다 다시 본다(캐시된 v3 를 믿지 않는다) */
export const lockSupportOfResponse = (body: unknown): 'v3' | 'legacy' => {
    const version = (body as { lockVersion?: unknown } | null | undefined)?.lockVersion;
    return typeof version === 'number' && version >= 3 ? 'v3' : 'legacy';
};

/**
 * 응답의 결정 원값(**저장 키**로 온다) → 섹션 키 기준으로 바꿔 읽는다.
 * 키별 boolean 여부로만 판단한다(형식이 틀리면 미정).
 */
export const readServerLockDecisions = (body: unknown): ServerLockDecisions => {
    const source = (body as { lockDecisions?: unknown } | null | undefined)?.lockDecisions;
    const out = { ...EMPTY_SERVER_LOCK_DECISIONS };
    DECISION_LOCKS.forEach(({ sectionKey, storageKey }) => {
        const value =
            source && typeof source === 'object' && !Array.isArray(source)
                ? (source as Record<string, unknown>)[storageKey]
                : undefined;
        out[sectionKey] = typeof value === 'boolean' ? value : null;
    });
    return out;
};

/**
 * 새 계약 응답 배열에서 화면이 쓸 목록을 만든다.
 *
 * 결정 키도 서버가 합친 배열을 따른다. 단, **서버 결정이 null(미정)인 브라우저 기록 키
 * (손실 시그널·기프티콘)에 이 계정의 브라우저 결정이 있으면 그 결정으로** 판정한다 — 이관(1회)
 * 전이거나 이관이 실패했을 때 서버 기본값이 브라우저에서 잠가 둔 것을 풀어 버리지 않게.
 * 스케줄 동작 키는 서버 배열 그대로다(브라우저 결정 없음).
 */
export const mergeServerLockDecisions = (
    serverSections: string[],
    serverDecisions: ServerLockDecisions,
    browserDecisions: ClientLockDecisions = {},
): string[] =>
    normalizeLockedSections([
        ...serverSections.filter((key) => !(DECISION_SECTION_KEYS as readonly string[]).includes(key)),
        ...DECISION_SECTION_KEYS.filter((key) => {
            if (
                (BROWSER_DECISION_KEYS as readonly string[]).includes(key) &&
                serverDecisions[key] === null &&
                typeof browserDecisions[key] === 'boolean'
            ) {
                return browserDecisions[key];
            }
            return serverSections.includes(key);
        }),
    ]);

/**
 * 이번 토글로 바뀐 결정 키만 고른다 — **섹션 키**로 계산한다.
 * 다른 기기가 다른 키를 바꾼 저장을 덮지 않기 위해, 안 바뀐 키는 보내지 않는다.
 * 보내기 직전에 `toStorageLockDecisions` 로 저장 키로 바꾼다.
 */
export const changedLockDecisions = (
    previousSections: string[],
    nextSections: string[],
): Partial<Record<DecisionSectionKey, boolean>> => {
    const changed: Partial<Record<DecisionSectionKey, boolean>> = {};
    DECISION_SECTION_KEYS.forEach((key) => {
        const before = isLockSectionLocked(previousSections, key);
        const after = isLockSectionLocked(nextSections, key);
        if (before !== after) changed[key] = after;
    });
    return changed;
};

/** 섹션 키 결정 → 저장 키 결정(전송 직전에만 쓴다) */
export const toStorageLockDecisions = (
    decisions: Partial<Record<DecisionSectionKey, boolean>>,
): Partial<Record<DecisionStorageKey, boolean>> => {
    const out: Partial<Record<DecisionStorageKey, boolean>> = {};
    DECISION_LOCKS.forEach(({ sectionKey, storageKey }) => {
        const value = decisions[sectionKey];
        if (typeof value === 'boolean') out[storageKey] = value;
    });
    return out;
};

/** 이 계정의 브라우저 결정에서 한 키를 지운다(서버가 결정을 가진 것이 확인된 뒤) */
export const clearClientLockDecision = (key: BrowserDecisionKey, accountId?: string | null) => {
    const decisions = readClientLockDecisions(accountId);
    if (!(key in decisions)) return;
    delete decisions[key];
    writeClientLockDecisions(decisions, accountId);
};

/** 이번 선택에서 클라이언트 전용 섹션이 어떻게 됐는지 기억한다 (열림도 결정이다) */
export const recordClientLockDecisions = (sections: string[], accountId?: string | null) => {
    const decisions = readClientLockDecisions(accountId);
    CLIENT_ONLY_LOCK_SECTION_KEYS.forEach((key) => {
        decisions[key] = sections.includes(key);
    });
    writeClientLockDecisions(decisions, accountId);
    return decisions;
};

/**
 * 서버로 보낼 목록.
 *
 * 클라이언트 전용 키는 빼고, 공지사항은 서버가 아는 이름으로 되돌린다.
 *
 * ⚠️ 공지사항 왕복에 함정이 하나 있다.
 *    화면은 공지를 `centerOps` 의 자식으로 두지만 **서버는 최상위 `notice`** 로 안다.
 *    `centerOps` 부모가 잠기면 화면은 자식 키를 전부 지우는데(정규화),
 *    그대로 보내면 서버에서 `notice` 가 사라져 **공지 탭 잠금이 풀린다.**
 *    그래서 `centerOps` 가 잠겨 있으면 `notice` 를 함께 실어 보낸다 —
 *    화면 기준으로 그때 공지도 잠긴 상태이므로 뜻이 같다.
 */
export const toServerLockedSections = (sections: string[]): string[] => {
    const mapped = sections
        .filter(
            (key) =>
                !CLIENT_ONLY_LOCK_SECTION_KEYS.includes(key) &&
                !(DECISION_SECTION_KEYS as readonly string[]).includes(key),
        )
        .map((key) => (key === 'centerOps.notice' ? 'notice' : key));
    if (mapped.includes('centerOps') && !mapped.includes('notice')) mapped.push('notice');
    return mapped;
};

export const normalizeLockedSections = (sections: string[]) => {
    const migratedSections = sections.map((section) => LEGACY_LOCKED_SECTION_KEY_MAP[section] ?? section);
    const uniqueSections = migratedSections.filter(
        (section, index) => migratedSections.indexOf(section) === index && VALID_LOCKED_SECTION_KEYS.includes(section),
    );
    const completeParentSections = LOCKABLE_SECTION_TREE.filter((section) => {
        const children = section.children ?? [];
        return children.length > 0 && children.every((child) => uniqueSections.includes(child.key));
    }).map((section) => section.key);
    const normalizedCandidates = uniqueSections.concat(completeParentSections);
    const lockedParents = new Set(normalizedCandidates.filter((section) => !section.includes('.')));

    return normalizedCandidates
        .filter((section, index) => normalizedCandidates.indexOf(section) === index)
        .filter((section) => {
            if (!section.includes('.')) return true;
            return !lockedParents.has(getParentSectionKey(section));
        });
};

export const isLockSectionLocked = (lockedSections: string[], sectionKey: string) => {
    if (!sectionKey.includes('.')) {
        return lockedSections.includes(sectionKey);
    }

    return lockedSections.includes(getParentSectionKey(sectionKey)) || lockedSections.includes(sectionKey);
};

export const getSectionLockState = (lockedSections: string[], section: LockableSection): LockState => {
    if (lockedSections.includes(section.key)) return 'locked';

    const children = section.children ?? [];
    if (children.some((child) => lockedSections.includes(child.key))) return 'partial';

    return 'unlocked';
};

const getSettlementLockKey = (search: string) => {
    const tab = new URLSearchParams(search).get('tab') ?? 'calendar';

    if (tab === 'classSales') return 'settlement.lessonSettlement';
    if (tab === 'salesList') return 'settlement.salesList';
    if (tab === 'salaryCalculation') return 'settlement.salary';
    // 세션 현황은 회원별 수업·잔여·금액이 함께 실려 급여 계산과 같은 잠금을 쓴다(설계 §7-1, Q7 기본값).
    if (tab === 'sessionReport') return 'settlement.salary';
    if (tab === 'unpaidList') return 'settlement.unpaidList';
    if (tab === 'rappoPaySettlement') return 'settlement';
    return 'settlement.salesCalendar';
};

export const resolveLockSectionFromPath = (pathname: string, search = '') => {
    if (pathname === '/settlement') return getSettlementLockKey(search);

    if (pathname === '/member/communication') return 'member.communicationBox';
    if (pathname === '/memberList' || pathname.startsWith('/member/detail') || pathname === '/member/temporary') {
        return 'member.memberManagement';
    }
    if (pathname.startsWith('/member/')) return 'member.memberManagement';
    if (pathname.startsWith('/publicMember/')) return 'member.memberManagement';
    if (pathname.startsWith('/trainerManage')) return 'member.coachManagement';

    if (pathname === '/schedule/history') return 'schedule.changeCancel';
    if (pathname.startsWith('/schedule')) return 'schedule.calendar';

    if (pathname === '/centerReport/instructorReviews') return 'centerReport.reviewStats';
    if (pathname === '/centerReport/centerStatistics') return 'centerReport.centerStats';
    if (pathname === '/centerReport/memberReviews') return 'centerReport.memberReviews';
    if (pathname === '/centerReport/newOpportunities') return 'centerReport.newOpportunity';
    if (pathname.startsWith('/centerReport/')) return 'centerReport.centerStats';

    if (pathname === '/facilityProduct') return 'centerOps.facilityProduct';
    if (pathname === '/facilitySubscription' || pathname === '/facilitySales') return 'centerOps.facilitySubscription';
    if (pathname.startsWith('/checkin')) return 'centerOps.checkin';
    if (pathname === '/locker') return 'centerOps.locker';
    if (pathname.startsWith('/kioskSettings')) return 'centerOps.kioskSettings';
    if (pathname === '/tabletPromotion') return 'centerOps.tabletPromotion';
    if (pathname === '/centerStats') return 'centerOps.centerStats';
    if (pathname.startsWith('/notice')) return 'centerOps.notice';
    if (pathname.startsWith('/gifticon')) return 'gifticon';
    /*
      데일리 브리핑. 마지막 fallback(pathname.split)으로도 같은 값이 나오지만,
      경로 규칙이 바뀌어도 잠금이 조용히 풀리지 않게 명시해 둔다.
    */
    if (pathname.startsWith('/opsBriefing')) return 'opsBriefing';

    if (pathname === '/group/settlement') return 'settlement.lessonSettlement';
    if (pathname.startsWith('/feed')) return 'feed';
    if (pathname.startsWith('/classPass')) return 'classPass';

    return pathname.split('/')[1] || 'root';
};

/**
 * 생성·수정 화면 경로가 요구하는 **동작** 잠금 키(보기 잠금 `schedule.calendar` 와 별개로 함께 본다).
 *
 * `/schedule/create` 는 이름과 달리 생성만 타지 않는다 — 「회원 추가하기」(type `oneMoreSchedule`)는
 * 기존 수업의 회원 구성 변경이라 수정이다. 신규기회 일정(type `newOpportunitySchedule`)은 수업이 아니라
 * 리드 기록이라 동작 잠금 대상이 아니다(보기만). 형식을 알 수 없으면 생성으로 본다(잠금 쪽).
 * 설계: rappo docs/SCHEDULE_ACTION_LOCK_DESIGN.md §6-4
 */
export const resolveScheduleRouteActionSection = (
    pathname: string,
    state?: unknown,
): ScheduleActionSectionKey | null => {
    const type = (state as { type?: unknown } | null | undefined)?.type;
    if (pathname === '/schedule/create') {
        if (type === 'newOpportunitySchedule') return null;
        if (type === 'oneMoreSchedule') return 'schedule.edit';
        return 'schedule.create';
    }
    if (pathname === '/schedule/update') return 'schedule.edit';
    return null;
};

/** 생성·수정 화면 경로인가 — 비밀번호를 취소하면 홈이 아니라 보던 화면(또는 스케줄)으로 돌아간다 */
export const isScheduleWriteRoute = (pathname: string) =>
    pathname === '/schedule/create' || pathname === '/schedule/update';
